const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
})

/** Formats an ISO date for display, returning undefined for absent or unparseable input. */
export const formatDate = (isoDate: string | null | undefined): string | undefined => {
  if (!isoDate) {
    return undefined
  }

  const parsed = new Date(isoDate)

  return Number.isNaN(parsed.getTime()) ? undefined : dateFormatter.format(parsed)
}

const pad = (value: number): string => String(value).padStart(2, "0")

/**
 * The duration chip on a session card — `3:22:10`, or `42:10` under an hour,
 * matching the TV app's `secondsToTime`. Undefined when the graph has no
 * duration, which is common on INT for sessions the account cannot play.
 */
export const formatDuration = (milliseconds: number | null | undefined): string | undefined => {
  if (!milliseconds || milliseconds <= 0) {
    return undefined
  }

  const totalSeconds = Math.floor(milliseconds / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

/**
 * How far through a session the viewer is, 0–100. Returns 0 rather than throwing
 * when either half of the ratio is missing — a card with no progress bar is the
 * right answer for a video that has never been played.
 */
export const progressPercent = (
  playheadSeconds: number | null | undefined,
  durationMilliseconds: number | null | undefined,
): number => {
  if (!playheadSeconds || !durationMilliseconds || durationMilliseconds <= 0) {
    return 0
  }

  const ratio = (playheadSeconds * 1000) / durationMilliseconds

  return Math.min(100, Math.max(0, Math.round(ratio * 100)))
}

/** Joins the metadata line under a detail title — `2025 | Discipleship | Beth Moore`. */
export const formatMetaLine = (parts: readonly (string | undefined)[]): string =>
  parts.filter((part): part is string => Boolean(part?.trim())).join("  |  ")

/** Joins author names the way the library cards display them. */
export const formatAuthors = (authors: readonly (string | null)[] | null | undefined): string | undefined => {
  const named = (authors ?? []).filter((author): author is string => Boolean(author?.trim()))

  return named.length > 0 ? named.join(", ") : undefined
}
